import { ThspianAvatar } from '../../Avatar/ThspianAvatar';
import CustomLazyLoadImage from '../../Image/CustomLazyLoadImage';
import { MAX_VISIBLE_ICONS } from '../../More/PlaylistComponents/PlaylistCardForGrid';
import { avatar_female, avatar_male } from '../../../asset/images';
import { CustomChipGroup } from '../../Chip/CustomChipGroup';
import { Box, Typography, type SxProps, type Theme } from '@mui/material';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { get } from 'lodash';

interface IGridColumnHeader {
    label: string;
    textSx?: SxProps<Theme>;
    icon?: React.ReactElement;
}

const getFieldValue = (params: any, field: string) => get(params.row, field);
interface ICustomImage {
    /**a custom image cell is required if image type is set to other */
    imageType?: 'profile' | 'project' | 'playlist' | 'campaign' | 'talent' | 'submissions' | 'other' | 'requests';
    params: any;
    device: any;
    field: string;
    onClick?: (params: any) => void;
}

interface IPillCell {
    label: string;
    textSx?: SxProps<Theme>;
}

const GridColumnHeader = ({ label, textSx, icon }: IGridColumnHeader): React.ReactElement => {
    return (
        <Box
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-end',
                justifyContent: 'flex-end',
                gap: '7px',
            }}>
            {icon}
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    fontSize: 12,
                    ...textSx,
                }}>
                {label}
            </Typography>
        </Box>
    );
};

const PillCell = ({ label, textSx }: IPillCell): React.ReactElement => {
    return (
        <Box
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-end',
            }}>
            <CustomChipGroup
                tagSx={{
                    px: '7px',
                    borderRadius: 13,
                    ...textSx,
                }}
                maxVisible={1}
                withNiceColor
                tags={label ? ['' + label] : ['N/A']}
            />
        </Box>
    );
};

const CustomImage = ({ imageType, params, device, field, onClick }: ICustomImage): React.ReactElement => {
    if (imageType === 'profile') {
        return <ThspianAvatar user={params.row} onClick={() => onClick && onClick(params.row)} />;
    }

    if (imageType === 'talent') {
        return (
            <CustomLazyLoadImage
                key={params.row._id}
                src={
                    params.row.talent.profile_picture
                        ? params.row.talent.profile_picture
                        : params.row.talent.gender === 'female'
                        ? avatar_female
                        : avatar_male
                }
                gradientStyle={{
                    borderRadius: 3,
                }}
                style={{
                    height: '65px',
                    minWidth: '60px',
                    width: '80px',
                    maxWidth: '100px',
                    border: `0.1px solid white`,
                    borderRadius: '10px',
                    objectFit: 'cover',
                    opacity: 1,
                    cursor: 'pointer',
                }}
                alt={params.row.title}
            />
        );
    }

    if (imageType === 'project' || imageType === 'campaign' || imageType === 'other' || imageType === 'submissions') {
        const value = getFieldValue(params, field);
        return (
            <CustomLazyLoadImage
                key={params.row._id}
                onClick={() => onClick && onClick(params.row)}
                src={value}
                gradientStyle={{
                    borderRadius: 3,
                }}
                style={{
                    height: '65px',
                    minWidth: '60px',
                    width: '80px',
                    maxWidth: '100px',
                    border: `0.1px solid white`,
                    borderRadius: '10px',
                    objectFit: 'cover',
                    opacity: 1,
                    cursor: onClick ? 'pointer' : 'none',
                }}
                noImageStyle={{
                    height: '65px',
                    minWidth: '60px',
                    width: '80px',
                    maxWidth: '100px',
                    border: `0.1px solid white`,
                    borderRadius: '10px',
                    objectFit: 'cover',
                    opacity: 1,
                }}
                alt={params.row.title}
            />
        );
    }

    return (
        <Box>
            {params.row.projects
                .slice(0, 3)
                .filter((p: { project: any; feature_image: any }) => p && p.project && !!p.feature_image)
                .map(
                    (
                        project: {
                            _id: React.Key | null | undefined;
                            feature_image: string | undefined;
                            title: string | undefined;
                        },
                        index: number,
                    ) => {
                        const size = '60px'; // Decrease size by 10px for each subsequent icon
                        const zIndex = MAX_VISIBLE_ICONS - index; // Control z-index to keep them stacked correctly

                        return (
                            <CustomLazyLoadImage
                                key={project._id}
                                onClick={() => onClick && onClick(params.row)}
                                src={project.feature_image}
                                gradientStyle={{
                                    borderRadius: 3,
                                }}
                                style={{
                                    height: size,
                                    width: size,
                                    zIndex: zIndex,
                                    position: 'relative', // Use relative position for stacking
                                    border: `0.1px solid white`,
                                    borderRadius: '10px',
                                    objectFit: 'cover',
                                    cursor: onClick ? 'pointer' : 'none',
                                    opacity: 1,
                                    ...(index === 0 &&
                                        params.row.projects.length === 2 && {
                                            top: 34,
                                        }),
                                    ...(index === 1 &&
                                        params.row.projects.length === 2 && {
                                            top: -30,
                                            width: device.isMobile ? '55px' : '55px',
                                            left: 3,
                                        }),
                                    ...(index === 0 &&
                                        params.row.projects.length > 2 && {
                                            top: 65,
                                            width: size,
                                        }),
                                    ...(index === 1 &&
                                        params.row.projects.length > 2 && {
                                            top: 1,
                                            width: device.isMobile ? '55px' : '55px',
                                            left: 3,
                                        }),
                                    ...(index === 2 && {
                                        bottom: 63,
                                        width: device.isMobile ? '50px' : '50px',
                                        left: 5,
                                    }),
                                }}
                                alt={project.title}
                            />
                        );
                    },
                )}
        </Box>
    );
};

export { GridColumnHeader, PillCell, CustomImage };
